import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { LeaveTypeEnum } from "@/types";
import { formatLeaveType } from "@/utils/leaveType";
import { Scale } from "lucide-react";

interface LeaveBalance {
  leaveType: string;
  year: number;
  allocated: number;
  used: number;
  remaining: number;
}

interface LeaveBalanceCardProps {
  balances?: LeaveBalance[];
}

export function LeaveBalanceCard({ balances }: LeaveBalanceCardProps) {
  const { user } = useAuth();
  const gender = user?.gender;

  const filteredBalances = balances?.filter((balance) => {
    if (balance.leaveType === LeaveTypeEnum.MATERNITY_LEAVE) {
      return gender === "FEMALE";
    }
    if (balance.leaveType === LeaveTypeEnum.PATERNITY_LEAVE) {
      return gender === "MALE";
    }
    return true;
  });

  return (
    <Card className="col-span-1 md:col-span-2 lg:col-span-3 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Scale className="h-5 w-5 text-indigo-500" />
          Leave Balances
        </CardTitle>
      </CardHeader>
      <CardContent>
        {filteredBalances && filteredBalances.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredBalances.map((balance, index) => (
              <div
                key={index}
                className="flex flex-col space-y-1 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="font-semibold text-sm">
                    {formatLeaveType(balance.leaveType)}
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {balance.year}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                  <div className="flex flex-col items-center p-1 bg-blue-50/50 dark:bg-blue-900/10 rounded">
                    <span className="text-muted-foreground text-[10px] uppercase">
                      Allocated
                    </span>
                    <span className="font-bold text-blue-600 dark:text-blue-400">
                      {balance.allocated}
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-1 bg-yellow-50/50 dark:bg-yellow-900/10 rounded">
                    <span className="text-muted-foreground text-[10px] uppercase">
                      Used
                    </span>
                    <span className="font-bold text-yellow-600 dark:text-yellow-400">
                      {balance.used}
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-1 bg-green-50/50 dark:bg-green-900/10 rounded">
                    <span className="text-muted-foreground text-[10px] uppercase">
                      Remaining
                    </span>
                    <span className="font-bold text-green-600 dark:text-green-400">
                      {balance.remaining}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            No leave balance data available
          </p>
        )}
      </CardContent>
    </Card>
  );
}
